import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export function PurgePanel() {
  const [purgeSource, setPurgeSource] = useState("email");
  const [purgeBefore, setPurgeBefore] = useState("");
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purging, setPurging] = useState(false);

  return (
    <div className="hairline rounded-lg p-5 bg-white" data-testid="settings-purge">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-red-500/10 border border-red-500/25 flex items-center justify-center">
          <Trash size={18} weight="duotone" className="text-red-400" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Purge tickets</div>
          <p className="text-xs text-slate-500 mt-1">
            Bulk-delete tickets (and their comments &amp; activity). Useful for clearing test data before going live. Preview first — nothing is deleted until you type <span className="font-mono text-slate-800">DELETE</span>.
          </p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Source</Label>
          <Select value={purgeSource} onValueChange={setPurgeSource}>
            <SelectTrigger className="mt-1 bg-[#FFFFFF] border-slate-200 h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="email">Only email-sourced tickets</SelectItem>
              <SelectItem value="web">Only web-created tickets</SelectItem>
              <SelectItem value="any">Any source</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Created before (optional)</Label>
          <Input
            type="datetime-local"
            value={purgeBefore}
            onChange={(e) => setPurgeBefore(e.target.value)}
            className="mt-1 bg-[#FFFFFF] border-slate-200 h-9"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase text-slate-500 tracking-wider">Type DELETE to confirm</Label>
          <Input
            value={purgeConfirm}
            onChange={(e) => setPurgeConfirm(e.target.value)}
            placeholder="DELETE"
            className="mt-1 bg-[#FFFFFF] border-slate-200 h-9 font-mono"
          />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button
          data-testid="settings-purge-preview"
          variant="outline"
          className="border-slate-200 bg-slate-100 h-9"
          disabled={purging}
          onClick={async () => {
            setPurging(true);
            try {
              const payload = {
                source: purgeSource === "any" ? null : purgeSource,
                before: purgeBefore ? new Date(purgeBefore).toISOString() : null,
                confirm: "",
                dry_run: true,
              };
              const r = await api.post("/admin/tickets/purge", payload);
              setPurgePreview(r.data);
              toast.success(`Preview: ${r.data.would_delete} ticket(s) would be deleted`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Preview failed");
            } finally { setPurging(false); }
          }}
        >Preview</Button>
        <Button
          data-testid="settings-purge-confirm"
          disabled={purgeConfirm !== "DELETE" || purging}
          onClick={async () => {
            if (!window.confirm(`Really delete ${purgePreview?.would_delete ?? "matching"} tickets? This cannot be undone.`)) return;
            setPurging(true);
            try {
              const payload = {
                source: purgeSource === "any" ? null : purgeSource,
                before: purgeBefore ? new Date(purgeBefore).toISOString() : null,
                confirm: "DELETE",
                dry_run: false,
              };
              const r = await api.post("/admin/tickets/purge", payload);
              toast.success(`Deleted ${r.data.deleted_tickets} tickets, ${r.data.deleted_comments} comments`);
              setPurgePreview(null);
              setPurgeConfirm("");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Purge failed");
            } finally { setPurging(false); }
          }}
          className="bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 h-9 gap-1"
        >
          <Warning size={14} /> Purge {purgePreview ? `(${purgePreview.would_delete})` : ""}
        </Button>
        {purgePreview && (
          <span className="text-xs text-slate-500 font-mono">
            would delete {purgePreview.would_delete} ticket{purgePreview.would_delete !== 1 && "s"}
          </span>
        )}
      </div>
    </div>
  );
}
